import type React from "react";
import { useEffect, useRef, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Box,
} from "@mui/material";
import type {
  IThumbnailTemplate,
  IStreamData,
} from "../../../../types/thumbnail-template";
import { useSelector } from "react-redux";
import TemplateRenderer from "./thumbnail-templates/TemplatesRenderer";
import ThumbnailPreviewDialog from "./Thumbnail";
import type { IStreamDetails } from "../../../../types/streams";
import { RootState } from "../../../../store/Index";
import apiService from "../../../../service/Api";
import domtoimage from "dom-to-image";

interface DynamicThumbnailCardProps {
  streamId?: number;
  streamData?: IStreamDetails | null;
  refreshTrigger?: number;
  width?: number;
  height?: number;
  onReadyDownload?: (fn: () => void) => void;
}

interface DeceasedThumbnailResponse {
  id: number;
  stream: IStreamData;
  thumbnail: IThumbnailTemplate;
  custom_background?: string;
}

const DynamicThumbnailCard: React.FC<DynamicThumbnailCardProps> = ({
  streamId,
  streamData,
  refreshTrigger = 0,
  width,
  height,
  onReadyDownload,
}) => {
  const [data, setData] = useState<DeceasedThumbnailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [thumbnailModalOpen, setThumbnailModalOpen] = useState(false);
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const [thumbnailType] = useState<string | null>("youtube_thumbnail");

  console.log("dynamic thumbnail ", streamData);

  const exportRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const fetchThumbnail = async () => {
      try {
        setLoading(true);

        const response = await apiService.get<{
          count: number;
          next: string | null;
          previous: string | null;
          results: DeceasedThumbnailResponse[];
        }>("/api/configurations/deceased-thumbnails/");

        const thumbnails = response.data.results;

        const matched = thumbnails.filter((thumb) => {
          return Number(thumb.stream.id) === Number(streamId);
        });

        setData(matched.length > 0 ? matched[0] : null);
      } catch (err) {
        console.error("Thumbnail fetch failed", err);
        setData(null);
      } finally {
        setLoading(false);
      }
    };

    if (streamId) {
      fetchThumbnail();
    }
  }, [streamId, refreshTrigger]);

  const handleOpenThumbnailModal = () => {
    setThumbnailModalOpen(true);
  };

  const handleCloseThumbnailModal = () => {
    setThumbnailModalOpen(false);
  };

  const handleSaveThumbnail = () => {
    if (streamId) {
      const fetchThumbnail = async (retryCount = 0) => {
        try {
          setLoading(true);

          await new Promise((resolve) => setTimeout(resolve, 500));

          const response = await apiService.get<{
            count: number;
            next: string | null;
            previous: string | null;
            results: DeceasedThumbnailResponse[];
          }>("/api/configurations/deceased-thumbnails/");

          const thumbnails = response.data.results;

          const matched = thumbnails.filter((thumb) => {
            return Number(thumb.stream.id) === Number(streamId);
          });

          if (matched.length > 0) {
            setData(matched[0]);
          } else if (retryCount < 2) {
            setTimeout(() => fetchThumbnail(retryCount + 1), 1000);
            return;
          } else {
            setData(null);
          }
        } catch (err) {
          console.error("Thumbnail fetch failed", err);
          if (retryCount < 2) {
            setTimeout(() => fetchThumbnail(retryCount + 1), 1000);
            return;
          }
        } finally {
          setLoading(false);
        }
      };
      fetchThumbnail();
    }
  };

  const handleDownloadThumbnail = async () => {
    try {
      if (!data) {
        console.error("No thumbnail data available");
        return;
      }

      if (!exportRef.current) {
        console.error("No thumbnail node found to export");
        return;
      }

      await new Promise((r) => setTimeout(r, 150));

      const width = 1280;
      const height = 720;

      const dataUrl = await domtoimage.toPng(exportRef.current, {
        width,
        height,
        cacheBust: true,
        bgcolor: "#000000",
      });

      const fileName = `thumbnail-stream-${streamId ?? "unknown"}.png`;

      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = fileName;
      link.click();
    } catch (err) {
      console.error("Thumbnail download failed", err);
    }
  };
  useEffect(() => {
    onReadyDownload?.(() => handleDownloadThumbnail());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, streamId]);

  const streamDataForModal: IStreamData =
    streamData && streamData.booking
      ? {
          id: streamId?.toString() || "0",
          booking_id: streamData.booking.id.toString(),
          bookingData: {
            booking_id: streamData.booking.id.toString(),
            booking_type: streamData.booking.booking_type as
              | "FUNERAL"
              | "CHURCH"
              | "FUNERAL_HOME"
              | "CONFERENCE",
            deceased_ids: streamData.booking.deceased[0]?.id
              ? [streamData.booking.deceased[0].id]
              : [],
            deceased_name: streamData.booking.deceased[0]?.first_name ?? "",
            deceased_second_name:
              streamData.booking.deceased[0]?.second_name ?? "",
            deceased_surname: streamData.booking.deceased[0]?.surname ?? "",
            deceased_photo_url:
              streamData.booking.deceased[0]?.person_image ?? "",
            deceased_picture_url:
              streamData.booking.deceased[0]?.person_image ?? "",
            deceased_dob: streamData.booking.deceased[0]?.date_of_birth ?? "",
            deceased_dod: streamData.booking.deceased[0]?.date_of_death ?? "",
          },
          title: streamData.title,
          date: streamData.date,
          shoot_from: streamData.start,
          location: streamData.service?.venue?.name ?? "",
          address: `${streamData.address_one}, ${streamData.city}`,
          venue_name: streamData.service?.venue?.name ?? "",
          custom_background: streamData?.stream_thumbnail_background_image,
        }
      : data?.stream || {
          id: streamId?.toString() || "0",
          booking_id: "0",
          bookingData: {
            booking_type: "FUNERAL",
            deceased_name: "Unknown",
          },
          title: "Loading...",
          date: "",
          shoot_from: "",
          location: "",
          address: "",
        };

  return (
    <>
      <Card
        onClick={handleOpenThumbnailModal}
        sx={{
          // height: "250px",
          // width: "350px",
          height: height,
          width: width,
          boxShadow: 0,
          transition: "all 0.3s ease",
          cursor: "pointer",
          "&:hover": {
            boxShadow: 2,
            transform: "translateY(-2px)",
          },
        }}
      >
        <CardContent>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
              <CircularProgress />
            </Box>
          ) : data ? (
            <Box
              sx={{
                // width: "100%",
                position: "relative",
                overflow: "hidden",
                width: width,
                height: height,
              }}
            >
              <Box
                sx={{
                  width: "1280px",
                  height: "720px",
                  transform: "scale(0.25)",
                  transformOrigin: "top left",
                }}
              >
                <div ref={exportRef} style={{ width: 1280, height: 720 }}>
                  <TemplateRenderer
                    streamData={streamDataForModal}
                    template={data.thumbnail}
                    customBackground={
                      streamData?.booking.booking_type === "CONFERENCE"
                        ? streamData.stream_thumbnail_background_image
                        : undefined
                    }
                    style={{
                      width: "1280px",
                      height: "720px",
                    }}
                  />
                </div>
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                p: 4,
                border: `2px dashed ${reduxTheme.primary_colors[300]}`,
                borderRadius: 2,
                minHeight: "200px",
              }}
            >
              <Typography variant="body2" color="error" gutterBottom>
                No thumbnail found for this stream.
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: reduxTheme.primary_colors[500],
                  textAlign: "center",
                }}
              >
                Click here to create a new thumbnail
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      <ThumbnailPreviewDialog
        open={thumbnailModalOpen}
        onClose={handleCloseThumbnailModal}
        streamData={streamDataForModal}
        onSave={handleSaveThumbnail}
        onDownload={handleDownloadThumbnail}
        refreshThumbnails={handleSaveThumbnail}
        thumbnailType={thumbnailType}
      />
    </>
  );
};

export default DynamicThumbnailCard;
